import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { parseISO, isBefore } from 'date-fns';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/hooks/useAuth'; // Keep your convention
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Building2, ArrowLeft } from 'lucide-react';

export default function PortalAuth() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, signIn } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [inviteToken] = useState(searchParams.get('token'));
  const [invitation, setInvitation] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('signin');

  // Check for invitation token on mount
  useEffect(() => {
    if (inviteToken) {
      fetchInvitation();
      setActiveTab('accept-invite');
    }
  }, [inviteToken]);

  // Redirect if already authenticated
  useEffect(() => {
    if (user && !loading) {
      const redirectTo = searchParams.get('redirectTo') || '/portal/dashboard';
      // console.log(
      //   '[PortalAuth] User authenticated, redirecting to:',
      //   redirectTo
      // );
      navigate(decodeURIComponent(redirectTo), { replace: true });
    }
  }, [user, loading, navigate, searchParams]);

  // Check for permission denied error
  useEffect(() => {
    if (searchParams.get('error') === 'permission_denied') {
      toast({
        variant: 'destructive',
        title: 'Access Denied',
        description:
          'Sorry, you do not have permission to login at this domain.',
      });
    }
  }, [searchParams, toast]);

  const fetchInvitation = async () => {
    try {
      // console.log('Invite token:', inviteToken);
      const { data, error } = await supabase
        .from('client_invitations')
        .select('*')
        .eq('token', inviteToken)
        .eq('status', 'pending')
        .single();

      // console.log('Invitation query data:', JSON.stringify(data, null, 2));
      // console.log('Invitation query error:', JSON.stringify(error, null, 2));

      if (error || !data) {
        setError('Invalid or expired invitation link');
        return;
      }

      const expiresAt = parseISO(data.expires_at);
      console.log('Parsed expires_at:', expiresAt, 'Current time:', new Date());
      console.log('Is expired:', isBefore(expiresAt, new Date()));

      if (isBefore(expiresAt, new Date())) {
        setError('This invitation has expired');
        return;
      }

      // Get client details separately
      // console.log('Fetching client for client_id:', data.client_id);
      const { data: clientData, error: clientError } = await supabase
        .from('clients')
        .select('id, name, domain, subdomain')
        .eq('id', data.client_id)
        .single();

      // console.log('Client query data:', JSON.stringify(clientData, null, 2));
      // console.log('Client query error:', JSON.stringify(clientError, null, 2));

      if (clientError || !clientData) {
        setError('Failed to load client details');
        // console.log('Falling back to default client name');
        setInvitation({
          ...data,
          clients: { name: 'Client Portal' },
        });
      } else {
        setInvitation({
          ...data,
          clients: clientData,
        });
      }
      setEmail(data.email);
    } catch (err) {
      setError('Failed to load invitation details');
      console.error('Fetch invitation error:', err);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { error } = await signIn(email, password);
      if (error) {
        throw error;
      }
      toast({
        title: 'Success',
        description: 'Signed in successfully',
      });
      // Redirect handled by useEffect
    } catch (err: any) {
      setError(err.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.functions.invoke(
        'accept-client-invitation',
        {
          body: { token: inviteToken, password },
        }
      );

      // Log response for debugging
      // console.log(
      //   'Accept invitation raw response:',
      //   JSON.stringify(data, null, 2)
      // );
      // console.log('Accept invitation error:', JSON.stringify(error, null, 2));

      if (error) {
        console.error('Edge function error:', error);
        throw new Error(error.message || 'Failed to accept invitation');
      }

      // Handle potential nested response from Supabase
      const responseData = data?.data || data;
      // console.log('Parsed response:', JSON.stringify(responseData, null, 2));

      // Validate response structure
      if (!responseData || !responseData.success) {
        console.error('Invalid response from edge function:', responseData);
        throw new Error('Invalid response from server');
      }

      toast({
        title: 'Success',
        description:
          responseData.message || 'Invitation accepted! You can now sign in.',
      });

      // Switch to sign-in tab
      setActiveTab('signin');
    } catch (err: any) {
      setError(err.message || 'Failed to accept invitation');
      console.error('Accept invitation error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-background via-background to-muted/50 flex items-center justify-center p-4'>
      <div className='w-full max-w-md space-y-6'>
        {/* Header */}
        <div className='text-center space-y-2'>
          <div className='mx-auto w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center'>
            <Building2 className='w-6 h-6 text-primary' />
          </div>
          <h1 className='text-2xl font-bold text-foreground'>
            {invitation ? `Join ${invitation.clients.name}` : 'Client Portal'}
          </h1>
          <p className='text-muted-foreground'>
            {invitation
              ? 'Accept your invitation to get started'
              : 'Sign in to access your client dashboard'}
          </p>
        </div>

        {/* Main Auth Card */}
        <Card>
          <CardHeader className='space-y-1'>
            <CardTitle className='text-xl text-center'>Welcome</CardTitle>
            <CardDescription className='text-center'>
              Access your personalized client portal
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className='grid w-full grid-cols-2'>
                <TabsTrigger value='signin'>Sign In</TabsTrigger>
                <TabsTrigger value='accept-invite' disabled={!inviteToken}>
                  Accept Invite
                </TabsTrigger>
              </TabsList>

              {/* Sign In Tab */}
              <TabsContent value='signin'>
                <form onSubmit={handleSignIn} className='space-y-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='email'>Email</Label>
                    <Input
                      id='email'
                      type='email'
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder='Enter your email'
                      required
                    />
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='password'>Password</Label>
                    <Input
                      id='password'
                      type='password'
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder='Enter your password'
                      required
                    />
                  </div>

                  {error && (
                    <Alert variant='destructive'>
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <Button type='submit' className='w-full' disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                        Signing in...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </form>
              </TabsContent>

              {/* Accept Invitation Tab */}
              <TabsContent value='accept-invite'>
                {invitation ? (
                  <form onSubmit={handleAcceptInvitation} className='space-y-4'>
                    <div className='bg-muted/50 p-3 rounded-lg'>
                      <p className='text-sm text-muted-foreground mb-1'>
                        You're invited to join:
                      </p>
                      <p className='font-medium'>
                        {invitation.clients?.name || 'Client'}
                      </p>
                      <p className='text-sm text-muted-foreground'>
                        Role: {invitation.role}
                      </p>
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='invite-email'>Email</Label>
                      <Input
                        id='invite-email'
                        type='email'
                        value={email}
                        disabled
                        className='bg-muted'
                      />
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='invite-password'>Set Password</Label>
                      <Input
                        id='invite-password'
                        type='password'
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder='Create a secure password'
                        required
                      />
                    </div>

                    {error && (
                      <Alert variant='destructive'>
                        <AlertDescription>
                          {error === 'User is banned' ? (
                            <p>
                              Your account has been disabled. Please contact us
                              for assistance.
                            </p>
                          ) : (
                            error
                          )}
                        </AlertDescription>
                      </Alert>
                    )}

                    <Button type='submit' className='w-full' disabled={loading}>
                      {loading ? (
                        <>
                          <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                          Accepting...
                        </>
                      ) : (
                        'Accept Invitation'
                      )}
                    </Button>
                  </form>
                ) : (
                  <div className='text-center py-6'>
                    <p className='text-muted-foreground'>
                      {error || 'Loading invitation details...'}
                    </p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
            <p className='text-center text-sm text-muted-foreground mt-4'>
              <Link
                to='/portal/forgot-password'
                className='text-blue-500 hover:underline'
              >
                Forgot your password?
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
